import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'

/**
 * The `+` on the صندوق‌ها tab — Android `BoxListFragment`'s `fabAdd` speed dial.
 *
 * It used to open «ساخت صندوق» directly, so a manager bringing a fund that
 * already runs outside Hamyan had no way to say so until the last modal of
 * the wizard. Android opens two actions instead; from the bottom up:
 * «انتقال صندوق به همیان» (`fabAddCashbox`, `ic_add_old_cashbox`), then
 * «ایجاد صندوق جدید» (`fabAddBox`, `ic_add_box`). The `+` turns into a close
 * icon and the screen behind goes 96% white (`cnsOpacity`, `#f5ffffff`);
 * tapping that closes the menu.
 *
 * Android is force-LTR, so each row is an LTR island: label on the left, the
 * button on the right edge, exactly as the app draws it.
 */
export default function AddFundSpeedDial() {
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    const toggle = () => {
        if (!open) posthog.capture('fund_add_menu_opened')
        setOpen(!open)
    }

    const start = (isTransfer: boolean) => {
        setOpen(false)
        posthog.capture('fund_create_origin_selected', {
            is_transfer: isTransfer,
            source: 'funds_fab',
        })
        navigate(isTransfer ? '/add-box?transfer=1' : '/add-box')
    }

    // Hidden actions stay mounted so they can fade and scale in like
    // Material's `show()`, but must not catch taps or focus while closed.
    // The column itself never catches taps: closed, it is a 200px-tall
    // invisible box over the bottom of the fund list.
    const actionClass = `transition-all duration-150 ${
        open
            ? 'opacity-100 scale-100 pointer-events-auto'
            : 'opacity-0 scale-50'
    }`

    return (
        <>
            {open && (
                <div
                    aria-hidden="true"
                    className="fixed inset-0 z-[19] bg-white/[0.96]"
                    onClick={() => setOpen(false)}
                />
            )}
            <div
                dir="ltr"
                // Offset by the safe area as well as the nav height: the nav
                // grows by that inset, so a flat 88px lets the button drift
                // into it wherever the inset is non-zero.
                className="pointer-events-none fixed bottom-[calc(88px+var(--safe-bottom))] right-4 z-30 flex flex-col items-end gap-4 md:right-[37%]"
            >
                <SpeedDialAction
                    label="ایجاد صندوق جدید"
                    open={open}
                    className={actionClass}
                    onClick={() => start(false)}
                    icon={<AddBoxIcon />}
                />
                <SpeedDialAction
                    label="انتقال صندوق به همیان"
                    open={open}
                    className={actionClass}
                    onClick={() => start(true)}
                    icon={<TransferFundIcon />}
                />
                <button
                    type="button"
                    aria-label={open ? 'بستن' : 'ساخت صندوق'}
                    aria-expanded={open}
                    className="pointer-events-auto cursor-pointer bg-green-1 rounded-full text-white w-14 h-14 flex items-center justify-center shadow-fab active:scale-95"
                    onClick={toggle}
                >
                    {open ? <CloseIcon /> : <PlusIcon />}
                </button>
            </div>
        </>
    )
}

function SpeedDialAction({
    label,
    icon,
    open,
    className,
    onClick,
}: {
    label: string
    icon: JSX.Element
    open: boolean
    className: string
    onClick: () => void
}) {
    return (
        <div
            className={`flex items-center gap-1 origin-right ${className}`}
            aria-hidden={!open}
        >
            <button
                type="button"
                tabIndex={open ? 0 : -1}
                onClick={onClick}
                className="p-2 text-green-1 text-base font-bold whitespace-nowrap"
            >
                {label}
            </button>
            <button
                type="button"
                tabIndex={open ? 0 : -1}
                aria-label={label}
                onClick={onClick}
                className="bg-green-1 rounded-full text-white w-14 h-14 flex items-center justify-center shadow-fab active:scale-95"
            >
                {icon}
            </button>
        </div>
    )
}

// The four vectors below are Android's own drawables, redrawn as-is.

/** `ic_plus`, drawn at `maxImageSize` 16dp */
function PlusIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M14,8H8V14H6V8H0V6H6V0H8V6H14V8Z" fill="currentColor" />
        </svg>
    )
}

/** `ic_new_close` */
function CloseIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path
                d="M14,1.41L12.59,0L7,5.59L1.41,0L0,1.41L5.59,7L0,12.59L1.41,14L7,8.41L12.59,14L14,12.59L8.41,7L14,1.41Z"
                fill="currentColor"
            />
        </svg>
    )
}

/** `ic_add_box`, 18dp */
function AddBoxIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path
                d="M7,10H1C0.735,10 0.48,10.105 0.293,10.293C0.105,10.48 0,10.735 0,11V17C0,17.265 0.105,17.52 0.293,17.707C0.48,17.895 0.735,18 1,18H7C7.265,18 7.52,17.895 7.707,17.707C7.895,17.52 8,17.265 8,17V11C8,10.735 7.895,10.48 7.707,10.293C7.52,10.105 7.265,10 7,10ZM6,16H2V12H6V16ZM17,0H11C10.735,0 10.48,0.105 10.293,0.293C10.105,0.48 10,0.735 10,1V7C10,7.265 10.105,7.52 10.293,7.707C10.48,7.895 10.735,8 11,8H17C17.265,8 17.52,7.895 17.707,7.707C17.895,7.52 18,7.265 18,7V1C18,0.735 17.895,0.48 17.707,0.293C17.52,0.105 17.265,0 17,0ZM16,6H12V2H16V6ZM17,13H15V11C15,10.735 14.895,10.48 14.707,10.293C14.52,10.105 14.265,10 14,10C13.735,10 13.48,10.105 13.293,10.293C13.105,10.48 13,10.735 13,11V13H11C10.735,13 10.48,13.105 10.293,13.293C10.105,13.48 10,13.735 10,14C10,14.265 10.105,14.52 10.293,14.707C10.48,14.895 10.735,15 11,15H13V17C13,17.265 13.105,17.52 13.293,17.707C13.48,17.895 13.735,18 14,18C14.265,18 14.52,17.895 14.707,17.707C14.895,17.52 15,17.265 15,17V15H17C17.265,15 17.52,14.895 17.707,14.707C17.895,14.52 18,14.265 18,14C18,13.735 17.895,13.48 17.707,13.293C17.52,13.105 17.265,13 17,13ZM7,0H1C0.735,0 0.48,0.105 0.293,0.293C0.105,0.48 0,0.735 0,1V7C0,7.265 0.105,7.52 0.293,7.707C0.48,7.895 0.735,8 1,8H7C7.265,8 7.52,7.895 7.707,7.707C7.895,7.52 8,7.265 8,7V1C8,0.735 7.895,0.48 7.707,0.293C7.52,0.105 7.265,0 7,0ZM6,6H2V2H6V6Z"
                fill="currentColor"
            />
        </svg>
    )
}

/** `ic_add_old_cashbox`, 24dp — a document with an arrow coming in */
function TransferFundIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
            <path
                d="M11,20H6C5.735,20 5.48,19.895 5.293,19.707C5.105,19.52 5,19.265 5,19V5C5,4.735 5.105,4.48 5.293,4.293C5.48,4.105 5.735,4 6,4H11V7C11,7.796 11.316,8.559 11.879,9.121C12.441,9.684 13.204,10 14,10H17V12C17,12.265 17.105,12.52 17.293,12.707C17.48,12.895 17.735,13 18,13C18.265,13 18.52,12.895 18.707,12.707C18.895,12.52 19,12.265 19,12V9C19,9 19,9 19,8.94C18.99,8.848 18.969,8.758 18.94,8.67V8.58C18.89,8.475 18.826,8.378 18.75,8.29L12.75,2.29C12.662,2.214 12.565,2.15 12.46,2.1C12.43,2.096 12.4,2.096 12.37,2.1L12.06,2H6C5.204,2 4.441,2.316 3.879,2.879C3.316,3.441 3,4.204 3,5V19C3,19.796 3.316,20.559 3.879,21.121C4.441,21.684 5.204,22 6,22H11C11.265,22 11.52,21.895 11.707,21.707C11.895,21.52 12,21.265 12,21C12,20.735 11.895,20.48 11.707,20.293C11.52,20.105 11.265,20 11,20ZM13,5.41L15.59,8H14C13.735,8 13.48,7.895 13.293,7.707C13.105,7.52 13,7.265 13,7V5.41ZM19,15H13.41L14.71,13.71C14.898,13.522 15.004,13.266 15.004,13C15.004,12.734 14.898,12.478 14.71,12.29C14.522,12.102 14.266,11.996 14,11.996C13.734,11.996 13.478,12.102 13.29,12.29L10.29,15.29C10.202,15.387 10.131,15.499 10.08,15.62C9.98,15.863 9.98,16.136 10.08,16.38C10.126,16.504 10.197,16.616 10.29,16.71L13.29,19.71C13.383,19.804 13.494,19.878 13.615,19.929C13.737,19.98 13.868,20.006 14,20.006C14.132,20.006 14.263,19.98 14.385,19.929C14.506,19.878 14.617,19.804 14.71,19.71C14.804,19.617 14.878,19.506 14.929,19.385C14.98,19.263 15.006,19.132 15.006,19C15.006,18.868 14.98,18.737 14.929,18.615C14.878,18.494 14.804,18.383 14.71,18.29L13.41,17H19C19.265,17 19.52,16.895 19.707,16.707C19.895,16.52 20,16.265 20,16C20,15.735 19.895,15.48 19.707,15.293C19.52,15.105 19.265,15 19,15Z"
                fill="currentColor"
            />
        </svg>
    )
}
